/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import VisAPIAdapter from './VisAPIAdapter';
import NodeDataSet from './NodeDataset';
import LinkDataset from './LinkDataset';
import { getDataVerificationResults, printMsg } from './RelationshipUtil';
import Config from './Config';

import { loadModules, disabledLoader } from 'util/ModuleLoader';
let VIPRUtils = null;
let QueryResultData = null;
let QueryResultObject = null;


/*External dependencies on dashboard-livewidget*/

//disabledLoader is used for unit testing scenario, so that dependencies are loaded upfront
if( disabledLoader ) {
	VIPRUtils = require('dashboard-analytics/visualizations/vipr/VIPRUtils');
	QueryResultData = require('dashboard-analytics/widgets/livewidget/query/QueryResultData');
	QueryResultObject = require('dashboard-analytics/widgets/livewidget/query/QueryResultObject');
}


/**
 *  This helper function should be called before instantiating/using DataProvider
 *  It loads Live Widget 3rd party dependencies used by DataProvider implementation
 **/
const loadDataProviderDeps = () => {
	return new Promise( (resolve, reject) => {
		loadModules(['dashboard-analytics/visualizations/vipr/VIPRUtils',
			'dashboard-analytics/widgets/livewidget/query/QueryResultData',
			'dashboard-analytics/widgets/livewidget/query/QueryResultObject'],( VIPRUtilsModule, QueryResultDataModule, QueryResultObjectModule) => {
			VIPRUtils = 	VIPRUtilsModule;
			QueryResultData = QueryResultDataModule;
			QueryResultObject = QueryResultObjectModule;
			resolve(true);
		}, reject);
	});
};

export {
	loadDataProviderDeps
};

const NODE_DATASET_LAYER = 'data.nodes';
const LINK_DATASET_LAYER = 'data.links';

/**
 * This class parses the data returned from upstream services, such as predict services and smart Services.
 * After that it reassembles the data as VIPRData structure that is used to render a VIDA Widget. (not 'live widget')
 **/
export default class DataProvider {

	/**
	 * @constructor
	 * @param {JSONObject} rawData, Data in original format that is returned from upstream services
	 * @param {Object} definition, definition object that is returned from VIDA APIs
	 * @param {Object} metadataInfo, metadata informaiton
	 * @param {Object} options, options to customize data on demand
	 */
	constructor (rawData, definition, metadataInfo, options){
		this._verifyData(rawData, options);
		this.visAPI = new VisAPIAdapter(definition);
		this.rawData = rawData;
		this.rawNodeDataset = new NodeDataSet(rawData, metadataInfo, options);
		this.rawLinkDataset = new LinkDataset(rawData, metadataInfo, options);
		//Cache the converted query result object
		this.nodeDataset = this.rawNodeDataset.getNodeDatasetQueryResult();
		this.linkDataset = this.rawLinkDataset.getLinkDatasetQueryResult();
	}
	/**
	 * @return Get the normalized data structure that can be consumed by VIPRUtils.
	 **/
	getData(){
		return VIPRUtils.createData(this.getNormalizedData(), this.visAPI);
	}

	_verifyData(rawData, options){
		const results = this.getDataVerificationResults(rawData, options);
		if(results){
			printMsg(results.linkDataset);
			printMsg(results.nodeDataset);
		}
	}

	/**
	*@return {JSONObject} information about the data verification, such as descriptive information or warnings, errors
	*/
	getDataVerificationResults(rawData, options){
		if(rawData){
			return getDataVerificationResults(rawData, options);
		}
		return null;
	}

	/**
	 * @return return the raw node dataset from the response
	 */
	getRawNodeDataset(){
		return this.rawNodeDataset;
	}

	/**
  * Get the normalized slot mapping that can be consumed by VIPRUtils.
  **/
	getSlotMapping() {
		return VIPRUtils.createSlotMapping(this.visAPI);
	}

	/*TODO: This function should call smart service and predict service to get the insights data*/
	getNormalizedData(){
		return this.getQueryResultObject().getQueryResults();
	}

	getQueryResultObject(){
		const oLinksData = new QueryResultData(this.linkDataset);
		const oNodesData = new QueryResultData(this.nodeDataset);
		const oQueryResultObject = new QueryResultObject();
		oQueryResultObject.addQueryResultObject(NODE_DATASET_LAYER, oNodesData );
		oQueryResultObject.addQueryResultObject(LINK_DATASET_LAYER, oLinksData );
		this._setDomain(oQueryResultObject, Config.domain_setting_dataItem, 1);
		return oQueryResultObject;
	}

	/*
	 *@param {Object} queryResultObject
	 *@param {String} mappedDataItem the data item name
	 *@param {Number} datasetIndex
   *@private
	 *Set the min and max value for strength data item in the dataset
	 */
	_setDomain(queryResultObject, mappedDataItem, datasetIndex){
		const domainSettings = {};
		domainSettings[mappedDataItem] = {
			min: Config.default_min_strength,
			max: Config.default_max_strength
		};
		queryResultObject.setTopBottomMappings(domainSettings, datasetIndex);
	}
}



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/relationshipGraph/DataProvider.js